"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader, ArrowRight, Mail, Phone, MapPin } from "lucide-react";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="w-12 h-12 rounded-lg bg-chart-4/10 flex items-center justify-center mb-6">
          <div className="w-2 h-2 rounded-md bg-chart-4" />
        </div>
        <h3 className="font-default text-2xl mb-2">Message Received</h3>
        <p className="text-muted-foreground mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-sm">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="rounded-sm border-x-0 border-t-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-ring px-0 shadow-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Enter your full name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="rounded-sm border-x-0 border-t-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-ring px-0 shadow-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs">We'll use this to reply to your message.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 7956 675231" className="rounded-sm border-x-0 border-t-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-ring px-0 shadow-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Optional – include a phone number if you prefer a call.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Subject</FormLabel>
              <FormControl>
                <Input placeholder="General Inquiry, Booking Question, etc." className="rounded-sm border-x-0 border-t-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-ring px-0 shadow-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Your Message</FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help you?" className="min-h-[120px] rounded-sm border-x-0 border-t-0 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-ring px-0 shadow-sm resize-none" {...field} />
            </FormControl>
            <FormDescription className="text-xs">Provide as much detail as possible.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto py-6 px-10 rounded-sm bg-foreground text-background hover:bg-foreground/90 transition-colors">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : <>
              Send Message
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function ReachOutToUs() {
  return <section id="reach-out-to-us" className="bg-background py-24 md:py-44 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          
          {}
          <div className="lg:col-span-6 relative flex flex-col justify-center">
            {}
            <div className="absolute top-0 bottom-0 -left-6 lg:-left-12 w-px bg-border hidden md:block" aria-hidden="true">
              <div className="absolute top-1/3 -left-[1.5px] w-1 h-1 rounded-md bg-foreground" />
            </div>

            <div className="space-y-12">
              <div>
                <span className="font-mono text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground mb-6 block">
                  Contact
                </span>
                <h2 className="font-default text-[clamp(2.5rem,6vw,4rem)] font-normal tracking-tight leading-[1.1] text-foreground">
                  <span className="block ml-8 md:ml-12 text-ring">Reach</span>
                  <span className="block">Out to Us</span>
                </h2>
              </div>

              <div className="space-y-8 pt-8 border-t border-border/50">
                <div className="flex items-start gap-4 group">
                  <div className="mt-1 p-2 rounded-md bg-muted/50 text-muted-foreground group-hover:text-ring transition-colors">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Direct Line</p>
                    <Link to="tel:07956675231" className="font-default text-2xl md:text-3xl hover:text-ring transition-colors">
                      07956675231
                    </Link>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="mt-1 p-2 rounded-md bg-muted/50 text-muted-foreground group-hover:text-ring transition-colors">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Email</p>
                    <Link to="mailto:hello@praisespaces.com" className="text-lg hover:text-ring transition-colors">
                      hello@praisespaces.com
                    </Link>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="mt-1 p-2 rounded-md bg-muted/50 text-muted-foreground group-hover:text-ring transition-colors">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Headquarters</p>
                    <p className="text-lg text-foreground/80">
                      Praise Space Ltd<br />
                      London, United Kingdom
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-6 relative">
            <div className="relative p-8 md:p-12 border border-border bg-background shadow-sm">
              {}
              <div className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-ring" aria-hidden="true" />
              <div className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-ring" aria-hidden="true" />
              <div className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-ring" aria-hidden="true" />
              <div className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-ring" aria-hidden="true" />

              <div className="mb-10">
                <h3 className="font-default text-2xl font-medium mb-2">Send a Message</h3>
                <p className="text-muted-foreground">We aim to respond to all inquiries within 24 hours.</p>
              </div>

              <FormProvider formId="reach_out_form_contact" sectionName="Reach Out to Us" defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thank you for reaching out. We have received your message and will be in touch shortly.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}